import React, { useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { PortWidget } from "@projectstorm/react-diagrams-core";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Context from "../../../../store/context";
import labels from "../helpers/labels";
import Condition from "../Conditions/Condition";
import {
  editAction,
  selectEventHandlers,
} from "../../../../store/slices/projectSlice";
import Errors from "../../../Components/Errors";

export default function EventListenerSummary({ eventListener, node, engine }) {
  const { addChild } = useContext(Context);
  const eventHandlers = useSelector(selectEventHandlers);
  const params = useParams();
  const dispatch = useDispatch();
  const eventHandler = eventHandlers.find(
    (eh) => eh.UUID === eventListener.EventHandlerUUID
  );

  return (
    eventHandler && (
      <div className="mt-2" key={eventHandler.UUID}>
        <Link
          className="btn btn-link px-0"
          to={`/library/logic/builder/${params.projectID}/workflow/${node.options.id}/event/${eventListener.EventType}/listener/${eventListener.UUID}`}
        >
          Listener: {eventHandler.Label} {eventHandler.IsModule && "(Module)"}
        </Link>
        <Errors
          filterFn={(e) =>
            e.eventHandler?.UUID === eventListener.EventHandlerUUID
          }
        />
        {[...(eventHandler.Rules || [])]
          ?.sort((a, b) => a.Order - b.Order)
          .map((rule) => (
            <table
              key={rule.UUID}
              className="table table-sm table-bordered m-0"
            >
              <tbody>
                <tr>
                  <td colSpan={2}>
                    <span className="fw-bold"> Rule: </span> {rule.Label}
                  </td>
                </tr>
                <tr>
                  <th>Conditions</th>
                  <th>Actions</th>
                </tr>
                <tr>
                  {/* Conditions */}
                  <td className="p-0">
                    <ul className="list-group list-group-flush condition-summary">
                      {rule.Conditions?.map((condition, index) => (
                        <li
                          key={condition.UUID}
                          className="list-group-item p-1"
                        >
                          <Condition
                            condition={condition}
                            setCondition={null}
                            index={index}
                            event={eventListener.EventType}
                          />
                        </li>
                      ))}
                    </ul>
                  </td>

                  {/* Actions */}
                  <td className="p-0">
                    <ul className="list-group list-group-flush">
                      {[...(rule.Actions || [])]
                        ?.sort((a, b) => a.Order - b.Order)
                        .map((action) => (
                          <li
                            key={`${eventListener.UUID}-${action.UUID}`}
                            className="list-group-item text-right p-1"
                          >
                            <Link
                              className="btn btn-link px-0 text-start"
                              to={`/library/logic/builder/${params.projectID}/workflow/${node.options.id}/event/${eventListener.EventType}/listener/${eventListener.UUID}/action/${action.UUID}`}
                            >
                              {labels[action.Type] || action.Type}
                            </Link>
                            {action.InheritContactSet ? (
                              <span className="badge text-bg-secondary badge-pill px-2 mx-2">
                                Pushes DCS
                              </span>
                            ) : (
                              ""
                            )}
                            {action.Type === "start:runtime" &&
                              (!action.IsSelf ? (
                                <>
                                  {
                                    // show add node button if nothing is connected

                                    !action.TypeUUID ? (
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm float-end"
                                        onClick={() => {
                                          const newUUID = uuidv4();
                                          dispatch(
                                            editAction({
                                              actionUUID: action.UUID,
                                              field: "TypeUUID",
                                              value: newUUID,
                                            })
                                          );
                                          addChild(
                                            {
                                              workflow: {
                                                Label: `Workflow-${newUUID.slice(
                                                  0,
                                                  4
                                                )}`,
                                                UUID: newUUID,
                                                IsEntrypoint: false,
                                                EventListeners: [],
                                              },
                                              eventHandlers,
                                            },
                                            node.options.name
                                          );
                                        }}
                                      >
                                        Add Node
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm float-end"
                                        onClick={() => {
                                          dispatch(
                                            editAction({
                                              actionUUID: action.UUID,
                                              field: "TypeUUID",
                                              value: "",
                                            })
                                          );
                                        }}
                                      >
                                        Delete link
                                      </button>
                                    )
                                  }
                                  {(() => {
                                    // check for port with pipe separator first, then dash separator for backward compatibility
                                    let portName = `${eventListener.UUID}|${action.UUID}`;
                                    let port = node.getPort(portName);
                                    if (!port) {
                                      portName = `${eventListener.UUID}-${action.UUID}`;
                                      port = node.getPort(portName);
                                    }
                                    if (port) {
                                      return (
                                        <PortWidget
                                          engine={engine}
                                          port={node.getPort(portName)}
                                          className="circle-porter circle-porter-out"
                                          action={action}
                                        >
                                          <div className="circle-port" />
                                        </PortWidget>
                                      );
                                    }
                                  })()}
                                </>
                              ) : (
                                " (SELF)"
                              ))}
                          </li>
                        ))}
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          ))}
      </div>
    )
  );
}
